import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export default function PdfModal({ urls, onClose, labels }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const bodyRef = useRef(null); 
    const [canvasWidth, setCanvasWidth] = useState(600);

    useEffect(() => {
        const el = bodyRef.current; 
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setCanvasWidth(Math.floor(entry.contentRect.width) - 32);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => { setPageNumber(1); setNumPages(null); }, [activeIdx]);

    const handleKey = useCallback((e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowRight") setPageNumber((p) => Math.min(numPages || 1, p + 1));
        if (e.key === "ArrowLeft") setPageNumber((p) => Math.max(1, p - 1));
    }, [onClose, numPages]);

    useEffect(() => {
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [handleKey]);

    const proxyUrl = (url) =>
        `/public/compliance/pdf-proxy?url=${encodeURIComponent(url)}`;

    return createPortal(
        <div className="pc-pdf-modal-overlay" onClick={onClose}>
            <div className="pc-pdf-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="pc-pdf-modal__header">
                    <span className="pc-pdf-modal__title">Certificate of Analysis</span>
                    <button className="pc-pdf-modal__close" onClick={onClose} aria-label="Close">&#10005;</button>
                </div>
                {urls.length > 1 && (
                    <div className="pc-pdf-modal__tabs">
                        {urls.map((_, i) => (
                            <button
                                key={i}
                                className={`pc-pdf-modal__tab${activeIdx === i ? " pc-pdf-modal__tab--active" : ""}`}
                                onClick={() => setActiveIdx(i)}
                            >
                                {labels?.[i] || `Document ${i + 1}`}
                            </button>
                        ))}
                    </div>
                )}
                <div className="pc-pdf-modal__body" ref={bodyRef}>
                    <div
                        className="pc-pdf-canvas-wrap"
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <Document
                            file={proxyUrl(urls[activeIdx])}
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            loading={<div className="pc-pdf-status">Loading PDF&hellip;</div>}
                            error={<div className="pc-pdf-status">Failed to load PDF.</div>}
                        >
                            <Page
                                pageNumber={pageNumber}
                                width={canvasWidth || 600}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                    </div>
                    {numPages && numPages > 1 && (
                        <div className="pc-pdf-nav">
                            <button
                                className="pc-pdf-nav__btn"
                                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                                disabled={pageNumber <= 1}
                            >&lsaquo;</button>
                            <span className="pc-pdf-nav__info">{pageNumber} / {numPages}</span>
                            <button
                                className="pc-pdf-nav__btn"
                                onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                                disabled={pageNumber >= numPages}
                            >&rsaquo;</button>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
